import * as CubiqTS from "shared/cubiq-model-multi-asset";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Injector,
  OnDestroy,
} from "@angular/core";
import { ICellRendererAngularComp } from "@bfx/ag-grid-angular";
import { LadderRate } from "../../../shared/cubiq-services/model/orders/LadderRate";
import { LadderPair } from "../../../shared/cubiq-services/model/orders/LadderPair";
import { IAfterGuiAttachedParams, ICellRendererParams } from "ag-grid-community";
import { BmarkRiskData } from "shared/cubiq-services/model/benchmarks/BmarkRiskData.model";
import { ProfilePromiseActionService, ProfileSettingKeys } from "shared/cubiq-services";
import { Subscription } from "rxjs";
import { OrderBlotterSavedSettingType } from "app/bfx-user-settings/bfx-order-blotter-settings-preferences.component";

const BUY_STR = "BUY";
const SELL_STR = "SELL";

/**
 * Consolidates existing Intent renders into a single component - handles intents
 * for BMs, Calls, Limits, Allocations, and Gamma Details
 * Supports values as OrderIntent, strings, booleans
 * Currently monitors the User profile for changes to intent styling settings.
 *
 * Modelled as 2 spans - the outer span, when styled, shows a gap between the Intent and the cell wall
 */
@Component({
  selector: "intent-renderer",
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: "directionRenderer" },
  template: ` <span
    [class]="{
      'intent-styled': intentStylingEnabled,
      'intent-unstyled': !intentStylingEnabled,
    }"
  >
    <span
      [ngStyle]="{ display: value != null && value.length > 0 && !hide ? '' : 'none' }"
      [class]="{
        'order-intent-buy': isBuy && intentStylingEnabled,
        'order-intent-sell': isSell && intentStylingEnabled,
        'order-intent-general': isUnstyled || !intentStylingEnabled,
      }"
      >{{ value }}</span
    >
  </span>`,
  standalone: false,
})
export class CommonIntentRenderer implements ICellRendererAngularComp, OnDestroy {
  // currentValue:IntentValue = new IntentValue();
  public isBuy: boolean;
  public isSell: boolean;
  public isUnstyled: boolean;
  public hide: boolean;
  public value: string;

  private settingSubscription: Subscription;
  public intentStylingEnabled: boolean = false;

  constructor(
    private injector: Injector,
    public cd: ChangeDetectorRef,
  ) {
    // this.initialiseIntentFormat();
  }

  /**
   * Read intent format form user settings and subscribe for changes
   */
  private initialiseIntentFormat() {
    const profileActionService = this.injector.get(ProfilePromiseActionService);
    if (profileActionService) {
      const key = ProfileSettingKeys.userProfileSettingsOrderBlotterPreferences;
      profileActionService.getProfileConfig(key).then((profileValue) => {
        this.initUserSettings(profileValue);
      });

      this.settingSubscription = profileActionService
        .getProfileConfigUpdates(key)
        .subscribe((profileValue) => {
          this.initUserSettings(profileValue);
        });
    }
  }

  /**
   * Initialise/update intent format
   * @param profileValue
   */
  private initUserSettings(profileValue: string) {
    if (typeof profileValue == "string") {
      const savedSettings: OrderBlotterSavedSettingType = JSON.parse(profileValue);
      const oldValue = this.intentStylingEnabled;
      this.intentStylingEnabled = savedSettings
        ? savedSettings.intentStylingEnabled === "y"
        : false;
      if (oldValue !== this.intentStylingEnabled) {
        this.cd.markForCheck();
      }
    }
  }

  ngOnDestroy(): void {
    if (this.settingSubscription) {
      this.settingSubscription.unsubscribe();
    }
  }

  // called on init
  agInit(params: ICellRendererParams): void {
    this.doVal(params);
  }

  doVal(params: ICellRendererParams): boolean {
    let intentStylingEnabled = this.intentStylingEnabled;
    if (params.context?.rendererSettings?.intentStylingEnabled !== undefined) {
      intentStylingEnabled = params.context.rendererSettings.intentStylingEnabled;
    } else if (this.settingSubscription == null) {
      this.initialiseIntentFormat();
    }

    let hide = false;
    if (params.data?.constructor) {
      hide =
        params.data.constructor.name == LadderRate.name ||
        params.data.constructor.name == LadderPair.name;
    }

    let value = params.value;
    let isBuy = false;
    let isSell = false;

    let isUnstyled = false;
    const risk = params.data.risk as BmarkRiskData;

    if (risk) {
      // Handle BMs where we may have net0 SSPs and near/far legs
      const intent = risk.isSwap ? risk.farIntent : risk.netIntent;
      isUnstyled = !risk.isSwap && risk.riskAmount.isZero();
      isBuy = !isUnstyled && intent === CubiqTS.OrderIntent.BUY;
      isSell = !isUnstyled && !this.isBuy;
    } else {
      if (params.data.orderType === CubiqTS.OrderType.LIMIT_CALL) {
        // For Calls, just use given text
        isBuy = false;
        isSell = false;
      } else if (params.data.orderState == CubiqTS.OrderState.GAMMA_MID_PENDING) {
        // Hide intent on GAMMA mid leg
        isBuy = false;
        isSell = false;
        value = "";
      } else if (typeof value === "number") {
        isBuy = value === CubiqTS.OrderIntent.BUY;
        isSell = value === CubiqTS.OrderIntent.SELL;
        value = isBuy ? BUY_STR : isSell ? SELL_STR : "";
      } else if (typeof value === "boolean") {
        isBuy = value;
        isSell = !value;
        value = isBuy ? BUY_STR : isSell ? SELL_STR : "";
      } else if (typeof value === "string") {
        const lcValue = value.toLowerCase();
        isBuy = lcValue === "buy";
        isSell = lcValue === "sell";
      }
      isUnstyled = !isBuy && !isSell;
    }

    const changed =
      isBuy !== this.isBuy ||
      isSell !== this.isSell ||
      isUnstyled !== this.isUnstyled ||
      hide !== this.hide ||
      value !== this.value ||
      intentStylingEnabled != this.intentStylingEnabled;
    if (changed) {
      this.hide = hide;
      this.isBuy = isBuy;
      this.isSell = isSell;
      this.isUnstyled = isUnstyled;
      this.value = value;
      this.intentStylingEnabled = intentStylingEnabled;
    }
    return changed;
  }

  // called when the cell is refreshed
  refresh(params: ICellRendererParams): boolean {
    if (this.doVal(params)) {
      this.cd.markForCheck();
    }

    return true;
  }
}
